import { Suspense, useEffect, useState } from "react";
import { NextApiRequest } from "next";
import type { AppContext, AppInitialProps, AppProps } from "next/app";
import getConfig from "next/config";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import Tealium from "@4tn/webx-analytics";
import { EmotionCache } from "@emotion/cache";
import { AppCacheProvider } from "@mui/material-nextjs/v13-pagesRouter";
import CssBaseline from "@mui/material/CssBaseline";
import Footer from "@navigation/Footer";
import SearchModal from "@navigation/SearchModal";
import TopNavigation from "@navigation/TopNavigation";
import { QueryClientProvider } from "@tanstack/react-query";
import ContentContainer from "@common/ContentContainer";
import CssVariables from "@common/CssVariables";
import ErrorBoundary from "@common/ErrorBoundary/ErrorBoundary";
import OutletStyles from "@host/OutletStyles/OutletStyles";
import OopsPage from "@pageContent/OopsPage";
import { ROUTE_PATHS, tableNames } from "@constants/consts";
import "@styles/globals.css";
import Theme, { GlobalStyles } from "@styles/theme";
import acceptAllConsent from "@utils/common/acceptAllConsent";
import detectAdBlock from "@utils/common/detectAdBlock";
import DynamoDBUtils from "@utils/common/dynamo";
import EventBus from "@utils/common/eventBus";
import featureTooling, { FeatureSlugs, useFeature } from "@utils/common/featureTooling";
import getHostConfig, { useHostConfig } from "@utils/common/getHostConfig";
import { setMainCategorySlugs } from "@utils/common/getPageCategory";
import isProdEnvironment from "@utils/common/isProdEnvironment";
import loadAppSmartBanner from "@utils/common/loadAppSmartBanner";
import { setCustomAttributes } from "@utils/common/newRelic";
import { queryClient } from "@utils/common/queryClient";
import removeHash from "@utils/navigation/removeHash";

const OutletSwitch = dynamic(() => import("@common/OutletSwitch"), { ssr: false });
const AudioPlayer = dynamic(() => import("@audio/AudioPlayer"));

const { publicRuntimeConfig } = getConfig();

interface Tealium {
  profile: string;
  environment: string;
}

interface InitialProps {
  hamburgerMenu?: Navigation | null;
  topNavigation?: Navigation | null;
  footer?: Navigation | null;
  initialCategories?: Category[] | null;
  initialStation?: AudioStationEntry | null;
  initialInApp?: boolean;
}
export interface HostAppProps extends AppProps, InitialProps {
  emotionCache?: EmotionCache;
}

export default function HostApp({
  Component,
  pageProps,
  hamburgerMenu,
  topNavigation,
  footer,
  initialCategories,
  initialStation,
  initialInApp,
  emotionCache,
}: HostAppProps) {
  const router = useRouter();
  const asPath = router.asPath;
  const [inApp] = useState(initialInApp);
  const [mainStation] = useState(initialStation);
  const { outletName, env, mfeName, tealium, appId, hostFile, designTokens, mainStationSlug } = useHostConfig();
  const { variables: smartBannerVariables } = useFeature<{ appToken: string }>(FeatureSlugs.APP_SMART_BANNER);

  const isOnHomePage = router.pathname === ROUTE_PATHS.HOME_PAGE;
  const isOnStationPage = router.pathname.startsWith(ROUTE_PATHS.STATIONS);
  const isOnPopoutPage = router.pathname.startsWith(ROUTE_PATHS.POPOUT);

  const shouldShowNavigation = !inApp && !isOnPopoutPage;

  useEffect(() => {
    if (smartBannerVariables?.appToken && !inApp) loadAppSmartBanner(smartBannerVariables.appToken);
  }, [smartBannerVariables, inApp]);

  useEffect(() => {
    if (window.location.href.includes("gdprBypass=true")) {
      acceptAllConsent();
    }

    if (!window.eventBus.initialized) {
      window.eventBus = new EventBus(window.eventBus);
    }

    if (tealium && typeof window.utag === "undefined") {
      Tealium.setup({
        account: "talpa",
        profile: tealium.profile,
        environment: tealium.environment,
        defaultLayer: {
          app_name: "webx platform",
          app_version: publicRuntimeConfig.version,
        },
      });

      detectAdBlock().then((detected) => {
        Tealium.setDataLayer({ adblock: detected });
      });
    }
  }, [tealium]);

  useEffect(() => {
    if (initialCategories) {
      setMainCategorySlugs(initialCategories.map(({ slug }) => slug));
      window.eventBus.dispatch("mainCategories", { categories: initialCategories });
    }
  }, [initialCategories]);

  useEffect(() => {
    window.eventBus.dispatch("hamburgerMenu", { isOpen: false });
  }, [asPath]);

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === "#consent") {
        window.OneTrust?.ToggleInfoDisplay();
        removeHash();
      }
    };

    handleHashChange();

    router.events.on("hashChangeComplete", handleHashChange);

    return () => {
      router.events.off("hashChangeComplete", handleHashChange);
    };
  }, [router.events]);

  useEffect(() => {
    featureTooling.reset(asPath);
  }, [asPath]);

  useEffect(() => {
    setCustomAttributes({ userAgent: window.navigator.userAgent });

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/service-worker.js", { scope: "/" }).catch((error) => {
        console.error("Service Worker registration failed:", error);
      });
    }
  }, []);

  return (
    <AppCacheProvider emotionCache={emotionCache}>
      <QueryClientProvider client={queryClient}>
        <Theme designTokens={designTokens}>
          <CssVariables />
          <Head>
            <title>{outletName}</title>
            <meta name="viewport" content="initial-scale=1, width=device-width" />
            {appId && <meta property="fb:appid" content={appId} />}
            <meta property="og:locale" content="nl_NL" />
            <meta property="og:site_name" content={outletName} />
            <meta property="og:logo" content={`/icons/${hostFile}/apple-icon-144x144.png`} />
            {asPath.startsWith(ROUTE_PATHS.ACCOUNT) && <meta name="robots" content="noindex" />}
          </Head>
          <CssBaseline />
          <OutletStyles />
          <GlobalStyles shouldShowNavigation={shouldShowNavigation} />
          {shouldShowNavigation && (
            <Suspense fallback={<></>}>
              <TopNavigation topNavigation={topNavigation} hamburgerMenu={hamburgerMenu} />
            </Suspense>
          )}
          <ErrorBoundary onError={() => <OopsPage />}>
            <ContentContainer>
              <Component {...pageProps} mainStation={mainStation} />
            </ContentContainer>
          </ErrorBoundary>
          {shouldShowNavigation && (
            <Suspense fallback={<></>}>
              <ErrorBoundary onError={() => <></>}>
                <SearchModal />
              </ErrorBoundary>
            </Suspense>
          )}
          {shouldShowNavigation && (
            <Suspense fallback={<></>}>
              <Footer footer={footer} />
            </Suspense>
          )}
          {(env !== "prod" || mfeName === "canary") && <OutletSwitch isAudioPlayerEnabled={!!mainStationSlug} />}
          {shouldShowNavigation && mainStation && (
            <AudioPlayer isOnHomePage={isOnHomePage} isOnStationPage={isOnStationPage} mainStation={mainStation} />
          )}
        </Theme>
      </QueryClientProvider>
    </AppCacheProvider>
  );
}

HostApp.getInitialProps = async ({ Component, ctx }: AppContext): Promise<AppInitialProps & InitialProps> => {
  const isServer = !!ctx.req;
  const inApp = ctx.query?.inApp === "true";
  const isOnPopoutPage = ctx.pathname.startsWith(ROUTE_PATHS.POPOUT);

  if (!Component.getInitialProps) return { pageProps: {}, initialInApp: inApp };

  if (!isServer) {
    return {
      pageProps: await Component.getInitialProps(ctx),
    };
  }

  const pageContext = isServer ? { ...ctx, dynamoDBUtils: DynamoDBUtils } : ctx;

  const { cookies } = ctx.req as NextApiRequest;

  const outlet = isProdEnvironment() ? undefined : cookies?.outlet;

  const hostConfig = getHostConfig(outlet);

  await featureTooling.fetchFeatures(hostConfig.featureFile);

  const pageProps = await Component.getInitialProps(pageContext);

  if (inApp || isOnPopoutPage) return { pageProps, initialInApp: inApp };

  const [hamburgerMenu, topNavigation, footer, initialCategories, initialStation] = await Promise.all([
    DynamoDBUtils.getItemBySlug<Navigation>(tableNames.navigation, "hamburger-menu").catch(() => null),
    DynamoDBUtils.getItemBySlug<Navigation>(tableNames.navigation, "tabbar-navigation").catch(() => null),
    DynamoDBUtils.getItemBySlug<Navigation>(tableNames.navigation, "footer").catch((error) => {
      console.log(error);
      return null;
    }),
    DynamoDBUtils.getMainCategories().catch((error) => {
      console.log(error);
      return null;
    }),
    hostConfig.mainStationSlug
      ? await DynamoDBUtils.getItemBySlug<AudioStationEntry>(tableNames.audio, hostConfig.mainStationSlug).catch(
          (error) => {
            console.log(error);
            return null;
          }
        )
      : null,
  ]);
  return {
    pageProps,
    hamburgerMenu,
    topNavigation,
    footer,
    initialCategories,
    initialStation,
    initialInApp: false,
  };
};
